<template>
	<DialogControl :model-value="modelValue" :title="title" :subtitle="subtitle" icon="inventory_2" :attach="overlayTarget || undefined" max-width="760" class="product-detail-dialog" @update:model-value="(value) => emit('update:modelValue', value)">
		<div v-if="detail" class="product-detail">
			<div class="product-detail__summary">
				<v-card flat border rounded="lg" :link="zoomable" class="product-detail__image" @click="openImage">
					<v-img v-if="thumbUrl" :src="thumbUrl" alt="" />
					<v-icon v-else size="48" class="product-detail__placeholder">inventory_2</v-icon>
					<v-icon v-if="zoomable" size="small" class="product-detail__zoom">zoom_in</v-icon>
				</v-card>
				<InfoTiles v-if="tiles.length" :tiles="tiles" stacked :min-width="120" class="product-detail__tiles" />
			</div>
			<div v-if="detail.description" class="product-detail__section">
				<div class="product-detail__heading">{{ t('Description') }}</div>
				<div class="product-detail__description" v-html="detail.description" />
			</div>
			<div v-if="sections.length" class="product-detail__section">
				<v-tabs v-model="tab" color="primary" density="compact">
					<v-tab v-for="section in sections" :key="section.key" :value="section.key">
						{{ section.label }}
						<v-chip size="x-small" class="ms-2">{{ section.count }}</v-chip>
					</v-tab>
				</v-tabs>
				<v-window v-model="tab">
					<v-window-item value="documents">
						<v-list density="compact">
							<v-list-item v-for="document in detail.documents" :key="document.id" :title="document.label" :subtitle="document.fileName !== document.label ? document.fileName : undefined" prepend-icon="description" rounded="lg" @click="openProductDocument(document.id)">
								<template #append>
									<v-btn icon="download" variant="text" size="small" :title="t('Download')" @click.stop="downloadProductDocument(document.id)" />
								</template>
							</v-list-item>
						</v-list>
					</v-window-item>
					<v-window-item value="components">
						<v-list density="compact">
							<v-list-item v-for="component in detail.components" :key="component.productId" rounded="lg">
								<ProductLabel :params="{ value: component.name, subtitle: component.ean, imageThumbnailUid: component.imageThumbnailUid, productId: component.productId, avatarSize: 40 }" />
								<template #append>
									<v-chip size="small" color="primary" label>× {{ component.quantity }} {{ component.container }}</v-chip>
								</template>
							</v-list-item>
						</v-list>
					</v-window-item>
				</v-window>
			</div>
			<ImageViewerDialog v-model="imageOpen" :url="imageUrl" :loading="imageLoading" :title="title" />
		</div>
		<div v-else class="text-medium-emphasis">{{ t('NoData') }}</div>
		<template #actions>
			<v-btn color="primary" variant="tonal" @click="emit('update:modelValue', false)">{{ t('Close') }}</v-btn>
		</template>
	</DialogControl>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import InfoTiles from '@/components/common/InfoTiles.vue';
import ProductLabel from '@/components/common/ProductLabel.vue';
import ImageViewerDialog from '@/components/dialogs/ImageViewerDialog.vue';
import { useTranslator } from '@/composables/useTranslator';
import { getFileUrl } from '@/api/core/file.js';
import { isOnline } from '@/sync/connectivity';
import { formatNumber } from '@/utils/core/format';
import { downloadProductDocument, openProductDocument, readProductDetail } from '@/utils/legacy/productDetail';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	productId: { type: Number, default: null },
});
const emit = defineEmits(['update:modelValue']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const detail = ref(null);
const tab = ref(null);
const thumbUrl = ref(null);
const imageOpen = ref(false);
const imageUrl = ref(null);
const imageLoading = ref(false);

const title = computed(() => detail.value?.name || '');
const subtitle = computed(() => [detail.value?.reference, detail.value?.ean].filter(Boolean).join(' · '));
const zoomable = computed(() => !!detail.value?.imageUid);

const dimension = (value) => (value === null ? null : formatNumber(value, { decimals: Number.isInteger(value) ? 0 : 2 }));

const tiles = computed(() => {
	if (!detail.value) return [];
	return [
		{ key: 'customerCode', label: t('CustomerCode'), display: detail.value.customerCode || null },
		{ key: 'weight', label: t('Weight'), display: dimension(detail.value.weight) },
		{ key: 'height', label: t('Height'), display: dimension(detail.value.height) },
		{ key: 'width', label: t('Width'), display: dimension(detail.value.width) },
		{ key: 'length', label: t('Length'), display: dimension(detail.value.length) },
	].filter((tile) => tile.display !== null);
});

const sections = computed(() =>
	[
		{ key: 'documents', label: t('Documents'), count: detail.value?.documents.length || 0 },
		{ key: 'components', label: t('BOM'), count: detail.value?.components.length || 0 },
	].filter((section) => section.count > 0)
);

watch(
	() => [props.modelValue, props.productId],
	async ([open, productId]) => {
		if (!open) return;
		detail.value = productId === null ? null : readProductDetail(productId);
		tab.value = sections.value[0]?.key || null;
		thumbUrl.value = null;
		const uid = detail.value?.imageThumbnailUid;
		const url = uid ? await getFileUrl(uid) : null;
		if (detail.value?.imageThumbnailUid === uid) thumbUrl.value = url;
	},
	{ immediate: true }
);

async function openImage() {
	if (!zoomable.value) return;
	const { imageUid, imageThumbnailUid } = detail.value;
	imageUrl.value = null;
	imageOpen.value = true;
	imageLoading.value = true;
	imageUrl.value = (isOnline() && (await getFileUrl(imageUid))) || (imageThumbnailUid ? await getFileUrl(imageThumbnailUid) : null);
	imageLoading.value = false;
}
</script>
